import { useEffect, useState } from "react";
import { HandCoins, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { callServiceAPI } from "@/lib/firebase/functions";
import { useAppStore } from "@/stores/app-store";

type LoanBody = {
    data?: { limit?: number; openLoan?: number; amount?: number };
    error?: { message?: string; };
}

async function callLoan(url: string, appCode: string) {
    const result = await callServiceAPI({ appCode, url, method: url === "get_credit_loan" ? 'get' : 'post', data: {} });
    const body = result.data as LoanBody | undefined;

    if(body?.error?.message) throw new Error(body.error.message);
    return body?.data ?? {};
}

export function CreditOptionSkeleton() {
    return (
        <div className="flex flex-col gap-3 rounded-xl border border-border bg-muted/20 p-3 lg:p-4">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-9 w-full" />
        </div>
    );
}

export function CreditLoanOffer({ onHold, onRelease, onSuccess }: { onHold?: () => void; onRelease?: () => void; onSuccess?: (amount: number) => void }) {
    const extensionName = useAppStore((s) => s.extensionName);
    const [limit, setLimit] = useState(0);
    const [openLoan, setOpenLoan] = useState(0);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if(!extensionName) return;
        callLoan('get_credit_loan', extensionName)
            .then((d) => {
                setLimit(d.limit ?? 0);
                setOpenLoan(d.openLoan ?? 0);
            })
            .catch(() => {})
            .finally(() => setLoaded(true));
    }, [extensionName]);

    async function takeLoan() {
        if(!extensionName) return;
        setSaving(true);
        setError(null);
        onHold?.();

        try {
            const d = await callLoan('take_credit_loan', extensionName);
            setOpenLoan(d.amount ?? limit);
            onSuccess?.(d.amount ?? limit);
        } catch(e) {
            setError((e as Error).message);
            onRelease?.();
        } finally {
            setSaving(false);
        }
    }

    if (!extensionName) return null;
    if (!loaded) return <CreditOptionSkeleton />;
    if (openLoan > 0) return null;

    if (limit <= 0) return null;

    return (
        <div className="animate-fade-in-up flex-1 flex flex-col gap-2 lg:gap-3 rounded-xl border border-border bg-muted/20 p-3 lg:p-4">
            <div className="flex items-center justify-between">
                <div className="flex h-8 w-8 lg:h-9 lg:w-9 items-center justify-center rounded-lg bg-primary/15">
                    <HandCoins className="h-4 w-4 text-primary" />
                </div>
                <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary">
                    Instant
                </span>
            </div>
            <div className="space-y-1">
                <p className="text-sm font-semibold text-foreground">Get {limit} credits instantly</p>
                <p className="text-xs text-muted-foreground">
                    Keep working right now. We'll take it back from your next renewal, no extra cost.
                </p>
            </div>
            {error && <p className="text-xs text-rose-600">{error}</p>}
            <div className="mt-auto">
                <Button variant="outline" size="sm" className="w-full" disabled={saving} onClick={takeLoan}>
                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                    Get credits now
                </Button>
            </div>
        </div>
    );
}